import type { ChatMessage, ChatInboxEmail, SentEmailData, ChoiceOption, Approval } from '../../shared/types/chat'
import type { AssistantMessageData } from './api'
import { formatDisplayTime } from '@tendbot/ui'

// ─── Choice Tag Parsing ────────────────────────────────────────

const CHOICE_TAG_RE = /\[choice\](.*?)\[\/choice\]/g

export interface ParsedChoices {
  choices: ChoiceOption[]
  cleanContent: string
}

/** Parse `[choice]Label[/choice]` tags from message text. Returns extracted choices + cleaned content. */
export function parseChoiceTags(content: string | undefined): ParsedChoices {
  if (!content) return { choices: [], cleanContent: '' }
  const choices: ChoiceOption[] = []
  let match: RegExpExecArray | null
  while ((match = CHOICE_TAG_RE.exec(content)) !== null) {
    choices.push({ id: `choice-${choices.length}`, label: match[1]!.trim() })
  }
  if (choices.length === 0) return { choices: [], cleanContent: content }
  const cleaned = content.replace(CHOICE_TAG_RE, '').replace(/\n{2,}$/g, '\n').trim()
  return { choices, cleanContent: cleaned }
}

// ─── External Content Parsing ──────────────────────────────────

const EXTERNAL_CONTENT_RE = /<(external-content|user-forwarded-content)\s+([^>]*)>([\s\S]*?)<\/\1>/
const SENT_EMAIL_RE = /<sent-email\s+([^>]*)>([\s\S]*?)<\/sent-email>/

function parseAttributes(attrString: string): Record<string, string> {
  const attrs: Record<string, string> = {}
  const re = /(\w[\w-]*)="([^"]*)"/g
  let m: RegExpExecArray | null
  while ((m = re.exec(attrString)) !== null) {
    attrs[m[1]!] = m[2]!
  }
  return attrs
}

export interface ParsedContent {
  email?: ChatInboxEmail
  sentEmail?: SentEmailData
  cleanContent: string
}

/** Parse `<external-content>`, `<user-forwarded-content>`, and `<sent-email>` from message text. */
export function parseExternalContent(content: string | undefined): ParsedContent {
  if (!content) return { cleanContent: '' }

  // Check for <sent-email> tag
  const sentMatch = content.match(SENT_EMAIL_RE)
  if (sentMatch) {
    const attrs = parseAttributes(sentMatch[1]!)
    const body = sentMatch[2]!.trim()
    let cleaned = content.replace(sentMatch[0], '').trim()
    // Strip common header line
    cleaned = cleaned.replace(/^(Email sent|Sent email)[^\n]*\n*/i, '').trim()

    return {
      sentEmail: {
        to: attrs.to ?? attrs['to-name'] ?? attrs['to-email']?.split('@')[0] ?? 'Unknown',
        toEmail: attrs['to-email'] ?? '',
        subject: attrs.subject ?? '(no subject)',
        preview: body,
        status: (attrs.status as 'sent' | 'delivered') ?? 'sent',
      },
      cleanContent: cleaned,
    }
  }

  // Check for <external-content> / <user-forwarded-content> tag
  const match = content.match(EXTERNAL_CONTENT_RE)
  if (!match) return { cleanContent: content }

  const tagName = match[1]!
  const attrs = parseAttributes(match[2]!)
  const body = match[3]!.trim()
  const isUserForward = tagName === 'user-forwarded-content'

  // Strip the content block and the header line
  let cleaned = content.replace(match[0], '').trim()
  cleaned = cleaned.replace(/^(New email from|Your user forwarded you an email:)[^\n]*\n*/i, '').trim()

  if (!isUserForward && attrs.source !== 'email') return { cleanContent: cleaned }

  return {
    email: {
      sender: attrs['sender-name'] ?? attrs.sender?.split('@')[0] ?? attrs.sender ?? 'Unknown',
      senderEmail: attrs.sender ?? '',
      subject: attrs.subject ?? '(no subject)',
      body,
      verified: isUserForward ? true : attrs.verified === 'true',
      status: attrs.status as 'processing' | 'no_action' | undefined,
    },
    cleanContent: cleaned,
  }
}

/** Returns true if the message content contains an <external-content>, <user-forwarded-content>, or <sent-email> block */
export function hasExternalContent(content: string | undefined): boolean {
  return !!content && (EXTERNAL_CONTENT_RE.test(content) || SENT_EMAIL_RE.test(content))
}


export function getRiskColor(risk: string): string {
  switch (risk) {
    case 'low': return 'border-emerald bg-emerald/10 text-emerald'
    case 'medium': return 'border-amber bg-amber/10 text-amber'
    case 'high': return 'border-rose bg-rose/10 text-rose'
    default: return 'border-slate-light bg-slate-mid/50'
  }
}

export function getRiskGlow(risk: string): string {
  switch (risk) {
    case 'low': return 'glow-risk-low'
    case 'medium': return 'glow-risk-medium'
    case 'high': return 'glow-risk-high'
    default: return ''
  }
}

export function getDateLabel(timestamp: string): string {
  const date = new Date(timestamp)
  const today = new Date()
  const todayStr = today.toDateString()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)
  const yesterdayStr = yesterday.toDateString()
  const dateStr = date.toDateString()
  if (dateStr === todayStr) return 'Today'
  if (dateStr === yesterdayStr) return 'Yesterday'
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
}

export function showDateSeparator(messages: ChatMessage[], index: number): boolean {
  if (index === 0) return true
  const prevMsg = messages[index - 1]
  const currMsg = messages[index]
  if (!prevMsg || !currMsg) return false
  const prev = new Date(prevMsg.timestamp).toDateString()
  const curr = new Date(currMsg.timestamp).toDateString()
  return prev !== curr
}

// ─── Approval Type Normalization ────────────────────────────────

/** Normalize raw actionType strings (from both builtin and plugin tools) into card-renderable types. */
function normalizeApprovalType(actionType: string): string {
  // Builtin types
  if (actionType === 'send-mail' || actionType === 'reply-email' || actionType === 'forward-email') {
    return 'email'
  }
  if (actionType === 'calendar-add') return 'calendar-add'
  if (actionType === 'calendar-update') return 'calendar-update'
  if (actionType === 'calendar-delete') return 'calendar-delete'
  if (actionType === 'booking') return 'booking'

  // Plugin tool names (e.g. plugin__google-workspace__send_gmail)
  const lower = actionType.toLowerCase()
  if (/gmail|email|mail/.test(lower)) return 'email'
  if (/calendar/.test(lower)) {
    if (/delete|remove/.test(lower)) return 'calendar-delete'
    if (/update|modify|edit/.test(lower)) return 'calendar-update'
    return 'calendar-add'
  }

  return 'generic'
}

/** Build human-readable details text for an approval card. */
function buildApprovalDetailsText(
  actionType: string,
  extendedInfo?: Record<string, unknown>,
): string {
  const info = extendedInfo as Approval['extendedInfo'] | undefined
  const parts: string[] = []

  if (info?.email?.subject) {
    parts.push(`Subject: ${info.email.subject}`)
  }

  // Calendar events: show event name and time details
  if (!parts.length && info?.event) {
    if (info.event.title) parts.push(info.event.title as string)
    if (info.changes?.newTime) parts.push(info.changes.newTime as string)
    else if (info.event.time) parts.push(info.event.time as string)
  }

  // Note: service badge ("via Microsoft 365") is rendered separately by ApprovalCard
  if (parts.length > 0) return parts.join(' · ')

  // Fallback: use normalized type description
  const type = normalizeApprovalType(actionType)
  switch (type) {
    case 'email': return 'Email action'
    case 'calendar-add': return 'Create calendar event'
    case 'calendar-update': return 'Update calendar event'
    case 'calendar-delete': return 'Delete calendar event'
    case 'booking': return 'Booking action'
    default: return `Action: ${actionType}`
  }
}

// ─── API Message → ChatMessage Mapping ──────────────────────────

import type { ApiMessage } from './api'

/** Map API messages into ChatMessage array. Used by both initial load and conversation switching. */
export function mapApiMessagesToChat(apiMessages: ApiMessage[]): ChatMessage[] {
  return apiMessages.map(m => {
    let msg: ChatMessage = {
      id: m.id,
      type: m.role as 'user' | 'assistant',
      content: m.content,
      timestamp: m.timestamp,
      displayTime: formatDisplayTime(m.timestamp),
      ...(m.approval && {
        approval: {
          id: m.approval.id,
          risk: m.approval.riskLevel,
          title: m.approval.summary,
          details: buildApprovalDetailsText(m.approval.actionType, m.approval.extendedInfo),
          status: m.approval.status,
          type: normalizeApprovalType(m.approval.actionType),
          ...(m.approval.extendedInfo && { extendedInfo: m.approval.extendedInfo }),
        },
      }),
      ...(m.sentEmail && { sentEmail: m.sentEmail }),
      ...(m.briefingCard && { briefingCard: m.briefingCard }),
      ...(m.triageEmail && { triageEmail: m.triageEmail }),
      ...(m.imageIds?.length && { imageIds: m.imageIds }),
      ...(m.sourceEmailId && { sourceEmailId: m.sourceEmailId }),
    }
    msg = applyPluginCards(msg, m.pluginCards)
    return msg
  })
}

// ─── Response → ChatMessage[] Mapping ──────────────────────────

/** Spread plugin card mappings onto a ChatMessage as dynamic keys (e.g. message.infoCard = data). */
function applyPluginCards(
  msg: ChatMessage,
  pluginCards?: Array<{ renderAs: string; data: unknown }>,
): ChatMessage {
  if (!pluginCards?.length) return msg
  const extras: Record<string, unknown> = {}
  for (const card of pluginCards) {
    extras[card.renderAs] = card.data
  }
  return { ...msg, ...extras }
}

/** Map an assistant message into an array of ChatMessages (main message + extra approval messages). */
export function buildMessagesFromResponse(response: AssistantMessageData): ChatMessage[] {
  const approvals: Approval[] = (response.approvals ?? []).map(a => ({
    id: a.approvalId,
    risk: a.riskLevel,
    title: a.summary,
    details: buildApprovalDetailsText(a.actionType, a.extendedInfo),
    status: a.status,
    type: normalizeApprovalType(a.actionType),
    ...(a.extendedInfo && { extendedInfo: a.extendedInfo }),
  }))

  const messages: ChatMessage[] = []

  // First message carries the text content + first approval (if any)
  let mainMessage: ChatMessage = {
    id: response.messageId,
    type: 'assistant',
    content: response.content,
    timestamp: response.timestamp,
    displayTime: formatDisplayTime(response.timestamp),
    ...(approvals[0] && { approval: approvals[0] }),
    ...(response.choiceOptions && { choiceOptions: response.choiceOptions }),
    ...(response.multiSelect != null && { multiSelect: response.multiSelect }),
    ...(response.sentEmail && { sentEmail: response.sentEmail }),
  }
  mainMessage = applyPluginCards(mainMessage, response.pluginCards)
  messages.push(mainMessage)

  // Additional approvals each get their own message
  for (let i = 1; i < approvals.length; i++) {
    messages.push({
      id: `${response.messageId}-approval-${i}`,
      type: 'assistant',
      timestamp: response.timestamp,
      displayTime: formatDisplayTime(response.timestamp),
      approval: approvals[i],
    })
  }

  return messages
}
